import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { Group, Panel, Separator, type Layout } from 'react-resizable-panels';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  useChatDocumentViewer,
  useHostedChatDocument,
} from '@/hooks/useChatDocumentViewer';
import {
  claimChatSurfaceAnchor,
  setChatViewing,
} from '@/chatContext/chatAppContextStore';
import {
  CHAT_ANCHOR_LABEL_TITLE_MAX_CHARS,
  toChatAnchorLabel,
} from '@/chatContext/chatAnchorLabels';
import useWindowWidth from '@/hooks/useWindowWidth';
import type { DocumentViewer } from '@/services/lectures';
import {
  setChatDocumentViewerStatus,
  updateChatDocumentViewerStatus,
} from '@/providers/chatDocumentViewerStatus';
import type { DocumentPdfViewerHandle } from '../../Documents/DocumentPdfViewer/DocumentPdfViewer';
import { firstActiveRegionOffset } from '../../Documents/DocumentPdfViewer/documentHighlights';
import DocumentViewerToolbar from '../../Documents/DocumentPdfViewer/DocumentViewerToolbar';
import LectureStudyMenu from '../../Documents/LectureStudyMenu';
import { useMcGrawHillDocument } from '../../../../mcgrawHill/McGrawHillContents'; // mcgraw-hill

const DocumentPdfViewer = lazy(
  () => import('../../Documents/DocumentPdfViewer/DocumentPdfViewer')
);

const DocumentSummarySidePane = lazy(
  () => import('../../Documents/DocumentSummary/DocumentSummarySidePane')
);

type WorkspacePane = 'document' | 'summary' | 'chat';

const PANE_LABELS: Record<WorkspacePane, string> = {
  document: 'Document',
  summary: 'Summary',
  chat: 'Chat',
};

type WorkspaceSurface = 'chat_page' | 'shell' | 'drawer';

/** A page request waiting for the document pane to be on screen. */
interface PendingPage {
  page: number;
  /** How far down the page its first highlighted region sits, if any. */
  offsetFraction: number | null;
}

const SPLIT_STORAGE_PREFIX = 'ora.chatDocumentViewer.split';

/** Narrowest the document pane gets beside the chat. */
export const DOCUMENT_MIN_WIDTH_PX = 400;
/** Narrowest the chat pane gets beside the document. */
export const CHAT_MIN_WIDTH_PX = 360;
/** The shell's document view inset (`_companion-shell.scss`, `has-document`). */
const SHELL_DOCUMENT_INSET_PX = 24;
/**
 * The AI desktop's document workspace (`_os-shell.scss`,
 * `.os-desktop__chat--document`): it starts 0.75rem right of the nav rail
 * (`--os-nav-width`, 5.125rem) and stops 0.75rem short of the right edge,
 * inside a 1px glass border.
 */
const DESKTOP_NAV_WIDTH_REM = 5.125;
const DESKTOP_DOCUMENT_INSET_REM = 0.75;
const DESKTOP_DOCUMENT_BORDER_PX = 1;

const rootFontSizePx = (): number =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

/**
 * The desktop workspace's inner width at this window width. The desktop's
 * chat column only takes this geometry once the document is open, so it is
 * derived rather than measured: a measurement taken before the document
 * class applies is the narrower column beside the history rail, and would
 * lay out the phone toggle (and mount the viewer there) for a commit.
 */
const desktopDocumentWidth = (windowWidth: number): number =>
  windowWidth -
  (DESKTOP_NAV_WIDTH_REM + DESKTOP_DOCUMENT_INSET_REM * 2) * rootFontSizePx() -
  DESKTOP_DOCUMENT_BORDER_PX * 2;

const loadSplit = (surface: WorkspaceSurface): [number, number] => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(`${SPLIT_STORAGE_PREFIX}.${surface}`) ?? 'null'
    );

    if (
      Array.isArray(parsed) &&
      parsed.length === 2 &&
      parsed.every((value) => typeof value === 'number')
    ) {
      return [parsed[0], parsed[1]];
    }
  } catch {
    // Storage can be unavailable in private browsing or a native webview.
  }

  return [58, 42];
};

const VIEWER_PANEL_ID = 'chat-document-viewer';
// react-resizable-panels 4 renders panel content in an `overflow: auto` div
// (v2 clipped with `overflow: hidden`); the chat column otherwise grows a
// horizontal scrollbar for anything wider than it. `overflow` is the one
// inline style the library lets a panel override.
const PANEL_STYLE = { overflow: 'hidden' } as const;
const CHAT_PANEL_ID = 'chat-document-chat';

const saveSplit = (surface: WorkspaceSurface, layout: Layout) => {
  // Only the two-pane layout is a preference; the chat alone is always 100.
  const viewer = layout[VIEWER_PANEL_ID];
  const chat = layout[CHAT_PANEL_ID];
  if (viewer === undefined || chat === undefined) return;
  const sizes: [number, number] = [viewer, chat];

  try {
    localStorage.setItem(
      `${SPLIT_STORAGE_PREFIX}.${surface}`,
      JSON.stringify(sizes)
    );
  } catch {
    // A persisted preference is optional; resizing still works for this visit.
  }
};

const inertWhen = (isInert: boolean) =>
  (isInert ? { inert: '' } : {}) as Record<string, string>;

const noop = () => {};

/**
 * The viewer's own frame while its chunk loads: the same toolbar (name still
 * to come, controls disabled, close working) over the same body, so the pane
 * is never an empty flash and nothing moves when the viewer takes over.
 */
const ViewerLoading = ({ onClose }: { onClose: () => void }) => (
  <div className="flex min-h-0 flex-1 flex-col bg-surface-muted">
    <DocumentViewerToolbar
      fileName={undefined}
      currentPage={null}
      numPages={0}
      canZoomOut={false}
      canZoomIn={false}
      onZoomOut={noop}
      onZoomIn={noop}
      onClose={onClose}
    />
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-fg-secondary"
      role="status"
      aria-live="polite"
    >
      <Spinner size={24} />
      <p className="text-sm">Loading document…</p>
    </div>
  </div>
);

/** The summary side pane while its chunk loads. */
const SummaryLoading = () => (
  <div
    className="flex flex-1 items-center justify-center text-fg-secondary"
    role="status"
    aria-label="Loading summary"
  >
    <Spinner size={20} />
  </div>
);

interface SessionView {
  sourceKey: string;
  isViewable: boolean;
  fileName: string | null;
  pages: [number, number] | null;
}

interface ChatDocumentWorkspaceProps {
  surface: WorkspaceSurface;
  /**
   * Whether this surface may show its document now. The AI desktop stays
   * mounted beneath windows; there it passes false and keeps to the chat
   * alone, even for the render before its document closes. Defaults to true.
   */
  isActive?: boolean;
  children: ReactNode;
}

/**
 * The chat with the side document viewer beside it (ORA-736).
 *
 * The chat subtree sits at the same place in the same elements whether a
 * document is open or not, on either side of 900px, so opening, closing or
 * resizing never remounts it: an unsent draft, a running follow-up and the
 * thread's scroll position all survive. Only the document pane (and the
 * phone toggle) come and go around it.
 */
const ChatDocumentWorkspace = ({
  surface,
  isActive = true,
  children,
}: ChatDocumentWorkspaceProps) => {
  const { close, setSidePane } = useChatDocumentViewer();

  // Each surface shows only the documents opened on it (the desktop's own, or
  // the companion's), and the desktop only while no window covers it.
  const hostedDocument = useHostedChatDocument(
    surface === 'chat_page' ? 'desktop' : 'companion'
  );

  const current = isActive ? hostedDocument : null;
  const windowWidth = useWindowWidth();

  // The width the two panes share once the document is open, known up front
  // for each surface so the first commit already has the right layout: the
  // shell grows to its inset document view, and the AI desktop's column to
  // its workspace beside the nav rail (where, at a small width, both panes
  // may not fit at their minimums and it gets the phone toggle instead).
  const availableWidth =
    surface === 'shell'
      ? windowWidth - SHELL_DOCUMENT_INSET_PX * 2
      : surface === 'chat_page'
        ? desktopDocumentWidth(windowWidth)
        : windowWidth;

  const isSinglePane =
    windowWidth <= 900 ||
    Capacitor.isNativePlatform() ||
    availableWidth < DOCUMENT_MIN_WIDTH_PX + CHAT_MIN_WIDTH_PX;

  const [activePane, setActivePane] = useState<WorkspacePane>('document');
  const [numPages, setNumPages] = useState(0);
  const [pendingPage, setPendingPage] = useState<PendingPage | null>(null);
  // What a lecture document session grounds on, as the viewer reports it for
  // one document: whether it is viewable (a locked lecture never grounds a
  // chat), its name, and the page window on screen. Tagged with the document,
  // so a report for the one before never grounds the next.
  const [sessionView, setSessionView] = useState<SessionView | null>(null);
  const viewerRef = useRef<DocumentPdfViewerHandle>(null);
  const mcgrawHill = useMcGrawHillDocument(current, viewerRef, close); // mcgraw-hill
  const documentViewRef = useRef<HTMLDivElement>(null);
  /** A session just opened here: focus moves into the document once shown. */
  const focusesDocument = useRef(false);
  /** The summary pane's Chat button: focus goes to the chat once shown. */
  const focusesChat = useRef(false);
  const chatBesideRef = useRef<HTMLDivElement>(null);
  const viewingTimer = useRef<number | null>(null);
  const [split] = useState(() => loadSplit(surface));
  const isOpen = current !== null;

  // The document's identity; a page request on the same document keeps it.
  const sourceKey = current
    ? current.source.kind === 'lecture'
      ? `lecture:${current.source.lectureId}`
      : `chatDocument:${current.source.documentId}`
    : null;

  // A lecture opened from its summary's citations shows that summary beside
  // it instead of the chat (ORA-812); on phones it is a third tab.
  const summaryLectureId =
    current?.sidePane === 'summary' && current.source.kind === 'lecture'
      ? current.source.lectureId
      : null;

  const showsSummary = summaryLectureId !== null;
  const showsSplit = isOpen && !isSinglePane;
  const showsToggle = isOpen && isSinglePane;

  // The Summary tab goes when the summary does (another opener asked for the
  // chat): its pane falls back to the document.
  const shownPane: WorkspacePane =
    activePane === 'summary' && !showsSummary ? 'document' : activePane;

  const panes: WorkspacePane[] = showsSummary
    ? ['document', 'summary', 'chat']
    : ['document', 'chat'];

  const isChatHidden = showsToggle && shownPane !== 'chat';
  const isDocumentHidden = showsToggle && shownPane !== 'document';
  const isSummaryHidden = showsToggle && shownPane !== 'summary';
  // Beside the document, the summary takes the chat's place; the chat stays
  // mounted under it, hidden, so the conversation never remounts.
  const isChatBesideHidden = showsSplit && showsSummary;

  const documentMinPercent = Math.max(
    40,
    (DOCUMENT_MIN_WIDTH_PX / availableWidth) * 100
  );

  const chatMinPercent = (CHAT_MIN_WIDTH_PX / availableWidth) * 100;

  const documentDefaultPercent = Math.min(
    100 - chatMinPercent,
    Math.max(documentMinPercent, split[0])
  );

  // The grounding and the status are shared with every chat surface, so only
  // the surface that showed the document clears them: a desktop mounting (or
  // unmounting) beneath the shell must leave the shell's document alone.
  const isHosting = useRef(false);

  useEffect(() => {
    if (viewingTimer.current !== null) {
      window.clearTimeout(viewingTimer.current);
      viewingTimer.current = null;
    }
    if (!current) {
      setPendingPage(null);
      if (!isHosting.current) return;
      isHosting.current = false;
      setChatViewing(null);
      setChatDocumentViewerStatus(null);
      return;
    }

    isHosting.current = true;

    focusesDocument.current =
      current.sessionLectureId !== null &&
      current.source.kind === 'lecture' &&
      current.source.lectureId === current.sessionLectureId;

    setChatDocumentViewerStatus({ fileName: null, page: null });
    // The page count is the viewer's to report: it resets it to 0 as it
    // starts loading a document and reports the real count once loaded.
    setActivePane('document');

    setChatViewing(
      current.source.kind === 'lecture'
        ? {
            kind: 'lecture',
            lectureId: current.source.lectureId,
            pages: null,
          }
        : {
            kind: 'chat_document',
            documentId: current.source.documentId,
            pages: null,
          }
    );
    // Keyed by the document, not the object carrying it.
  }, [sourceKey]);

  // A page request (a citation) lands on the Document pane on phones.
  useEffect(() => {
    if (!current?.requestedPage) return;
    setActivePane('document');

    // A highlighted request aims at its first region, not the page's top.
    setPendingPage({
      page: current.requestedPage,
      offsetFraction: firstActiveRegionOffset(current.requestedHighlight),
    });
  }, [current?.requestId, current?.requestedPage]);

  // Scroll only once the pane is on screen: a hidden (`display: none`) pane has
  // no layout to scroll to. Effects run after the commit that showed it.
  useEffect(() => {
    if (pendingPage === null || numPages === 0 || isDocumentHidden) return;

    const { page, offsetFraction } = pendingPage;

    if (offsetFraction) {
      viewerRef.current?.scrollToPage(page, { offsetFraction });
    } else {
      viewerRef.current?.scrollToPage(page);
    }

    setPendingPage(null);
  }, [isDocumentHidden, numPages, pendingPage]);

  // View Document moves focus into the document it opened (its toolbar comes
  // first), once the pane is on screen; the provider hands it back to the
  // opener when the student closes the document.
  useEffect(() => {
    if (!focusesDocument.current || isDocumentHidden) return;
    const documentView = documentViewRef.current;
    if (!documentView) return;

    focusesDocument.current = false;
    documentView.focus({ preventScroll: true });
  });

  // The Chat button unmounts with the summary pane; focus goes to the chat's
  // composer (or the chat itself) rather than falling to the page.
  useEffect(() => {
    if (!focusesChat.current || isChatBesideHidden) return;
    focusesChat.current = false;
    const chat = chatBesideRef.current;
    if (!chat) return;

    const composer = chat.querySelector<HTMLElement>(
      'textarea:not([disabled])'
    );

    (composer ?? chat).focus({ preventScroll: true });
  });

  const showChat = useCallback(() => {
    focusesChat.current = true;
    setSidePane('chat');
  }, [setSidePane]);

  useEffect(
    () => () => {
      if (viewingTimer.current !== null) {
        window.clearTimeout(viewingTimer.current);
      }
      if (!isHosting.current) return;
      setChatViewing(null);
      setChatDocumentViewerStatus(null);
    },
    []
  );

  const reportView = useCallback(
    (key: string | null, patch: Partial<Omit<SessionView, 'sourceKey'>>) => {
      if (!key) return;

      setSessionView((view) => ({
        ...(view?.sourceKey === key
          ? view
          : { sourceKey: key, isViewable: false, fileName: null, pages: null }),
        ...patch,
      }));
    },
    []
  );

  const reportFileName = useCallback(
    (fileName: string | null) => {
      reportView(sourceKey, { fileName });
      updateChatDocumentViewerStatus({ fileName });
    },
    [reportView, sourceKey]
  );

  const reportMeta = useCallback(
    (meta: DocumentViewer | null) =>
      reportView(sourceKey, { isViewable: meta?.accessState === 'viewable' }),
    [reportView, sourceKey]
  );

  const reportVisiblePage = useCallback(
    (page: number | null) => updateChatDocumentViewerStatus({ page }),
    []
  );

  const publishPages = useCallback(
    (pages: [number, number] | null) => {
      if (!current) return;
      if (viewingTimer.current !== null) {
        window.clearTimeout(viewingTimer.current);
      }

      viewingTimer.current = window.setTimeout(() => {
        reportView(sourceKey, { pages });

        setChatViewing(
          current.source.kind === 'lecture'
            ? {
                kind: 'lecture',
                lectureId: current.source.lectureId,
                pages,
              }
            : {
                kind: 'chat_document',
                documentId: current.source.documentId,
                pages,
              }
        );
      }, 300);
    },
    [current, reportView, sourceKey]
  );

  // View Document's session (ORA-736): the provider claims the lecture as the
  // companion's surface anchor the moment the session opens, which moves the
  // companion to the lecture's own thread. While the viewer shows it, the
  // claim follows the pages on screen and, once the lecture is viewable, its
  // name — neither changes the thread. The provider releases the claim when
  // the document closes; a surface that stops hosting it (the study drawer
  // handing over to the shell) leaves it in place.
  const sessionLectureId =
    current?.sessionLectureId &&
    current.source.kind === 'lecture' &&
    current.source.lectureId === current.sessionLectureId
      ? current.sessionLectureId
      : null;

  useEffect(() => {
    if (!sessionLectureId || sessionView?.sourceKey !== sourceKey) return;

    // A locked lecture's viewer names it "Document"; only a viewable one's
    // name labels what the chat is about.
    const label = sessionView.isViewable
      ? toChatAnchorLabel(
          'Lecture',
          sessionView.fileName,
          CHAT_ANCHOR_LABEL_TITLE_MAX_CHARS
        )
      : undefined;

    claimChatSurfaceAnchor({
      type: 'lecture',
      lectureId: sessionLectureId,
      pages: sessionView.pages,
      ...(label ? { label } : {}),
    });
  }, [sessionLectureId, sessionView, sourceKey]);

  const viewer = current ? (
    // Focusable so View Document can move focus here; marked as the viewer
    // so Escape from here closes the document, as from inside the viewer.
    <div
      ref={documentViewRef}
      tabIndex={-1}
      role="group"
      aria-label="Document"
      data-chat-document-viewer
      className="flex min-h-0 flex-1 flex-col outline-none"
    >
      <Suspense fallback={<ViewerLoading onClose={close} />}>
        <DocumentPdfViewer
          ref={viewerRef}
          source={current.source}
          examId={current.lectureExam?.examId}
          schoolDomains={current.lectureExam?.schoolDomains}
          toolbarActions={
            // mcgraw-hill: study starts from pills above the chat input.
            current.source.kind === 'lecture' && !mcgrawHill.formatFileName ? (
              <LectureStudyMenu lectureId={current.source.lectureId} />
            ) : undefined
          }
          onNumPagesChange={setNumPages}
          onCurrentPagesChange={publishPages}
          onFileNameChange={reportFileName}
          // mcgraw-hill: "Chapter 12 - <lecture name>" in place of "Ch12.pdf".
          formatFileName={mcgrawHill.formatFileName}
          onMetaChange={reportMeta}
          onVisiblePageChange={reportVisiblePage}
          highlight={current.requestedHighlight}
          highlightKey={current.requestId}
          onClose={close}
        />
      </Suspense>
    </div>
  ) : null;

  const summaryPane = (lectureId: string, onShowChat?: () => void) => (
    <Suspense fallback={<SummaryLoading />}>
      <DocumentSummarySidePane
        lectureId={lectureId}
        exam={current?.lectureExam}
        onShowChat={onShowChat}
      />
    </Suspense>
  );

  return (
    <div
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 flex-1 flex-col',
        isOpen && 'bg-surface-canvas'
      )}
      role={isOpen ? 'region' : undefined}
      aria-label={isOpen ? 'Document and chat' : undefined}
    >
      {showsToggle ? (
        <div
          className="flex shrink-0 border-b border-border-subtle bg-surface-canvas"
          role="tablist"
          aria-label="Document viewer panes"
        >
          {panes.map((pane) => (
            <button
              key={pane}
              type="button"
              role="tab"
              aria-selected={shownPane === pane}
              onClick={() => setActivePane(pane)}
              className={cn(
                'min-h-11 flex-1 border-b-2 border-transparent px-3 py-2 text-sm font-medium text-fg-secondary transition-colors',
                shownPane === pane && 'border-fg-primary text-fg-primary'
              )}
            >
              {PANE_LABELS[pane]}
            </button>
          ))}
        </div>
      ) : null}
      {showsToggle ? (
        <div
          className={cn(
            'min-h-0 flex-1 flex-col',
            isDocumentHidden ? 'hidden' : 'flex'
          )}
          data-testid="chat-document-pane"
          aria-hidden={isDocumentHidden}
          {...inertWhen(isDocumentHidden)}
        >
          {viewer}
        </div>
      ) : null}
      {showsToggle && summaryLectureId ? (
        <div
          className={cn(
            'min-h-0 flex-1 flex-col',
            isSummaryHidden ? 'hidden' : 'flex'
          )}
          data-testid="chat-summary-pane"
          aria-hidden={isSummaryHidden}
          {...inertWhen(isSummaryHidden)}
        >
          {summaryPane(summaryLectureId)}
        </div>
      ) : null}
      {/* The chat's place: always this element, this group, this panel. */}
      <div
        className={cn(
          'min-h-0 flex-1 flex-col',
          isChatHidden ? 'hidden' : 'flex'
        )}
        data-testid="chat-conversation-pane"
        aria-hidden={isChatHidden || undefined}
        {...inertWhen(isChatHidden)}
      >
        <Group
          orientation="horizontal"
          className="min-h-0 flex-1"
          // Only the two-pane layout resizes; see ExamView for why the
          // single-pane group is disabled rather than merely handle-less.
          disabled={!showsSplit}
          onLayoutChanged={(layout) => saveSplit(surface, layout)}
        >
          {showsSplit ? (
            <Panel
              id={VIEWER_PANEL_ID}
              defaultSize={`${documentDefaultPercent}%`}
              minSize={`${documentMinPercent}%`}
              style={PANEL_STYLE}
            >
              <div className="flex h-full min-h-0">
                {/* mcgraw-hill: the textbook's contents beside the chapter. */}
                {mcgrawHill.contents}
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  {viewer}
                </div>
              </div>
            </Panel>
          ) : null}
          {showsSplit ? (
            <Separator className="group relative w-1 bg-border-subtle transition-colors hover:bg-fg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default">
              <span className="absolute inset-y-0 left-1/2 w-3 -translate-x-1/2" />
            </Separator>
          ) : null}
          <Panel
            id={CHAT_PANEL_ID}
            defaultSize={
              showsSplit ? `${100 - documentDefaultPercent}%` : '100%'
            }
            minSize={showsSplit ? `${chatMinPercent}%` : undefined}
            style={PANEL_STYLE}
          >
            <div
              className={cn(
                'flex h-full min-h-0 flex-col',
                showsSplit && 'border-l border-border-subtle'
              )}
            >
              {showsSplit && summaryLectureId ? (
                <div
                  className="flex min-h-0 flex-1 flex-col"
                  data-testid="chat-summary-pane"
                >
                  {summaryPane(summaryLectureId, showChat)}
                </div>
              ) : null}
              <div
                ref={chatBesideRef}
                // Focusable only by script: where the Chat button sends focus
                // when the chat has no composer to take it.
                tabIndex={-1}
                className={cn(
                  'min-h-0 flex-1 flex-col outline-none',
                  isChatBesideHidden ? 'hidden' : 'flex'
                )}
                aria-hidden={isChatBesideHidden || undefined}
                {...inertWhen(isChatBesideHidden)}
              >
                {children}
              </div>
            </div>
          </Panel>
        </Group>
      </div>
    </div>
  );
};

export default ChatDocumentWorkspace;
